import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const EMOTION_COLORS = {
  calm: '#4ade80',       // brighter green
  confident: '#34d399',  // emerald
  neutral: '#fbbf24',    // brighter amber
  anxious: '#f87171',    // brighter red
  frustrated: '#fb7185', // rose
  excited: '#fb923c',    // brighter orange
};

const PNL_COLORS = {
  positive: '#4ade80',
  negative: '#f87171',
  zero: '#a1a1aa',
};

// Distinct gray that's clearly visible on dark backgrounds
const NO_DATA_COLOR = '#3f3f46';
// Color for days with trades but no journal entry
const TRADED_NO_JOURNAL_COLOR = '#60a5fa'; // blue - shows there was a trade

const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const EmotionalHeatmap = ({ data = [] }) => {
  const [mode, setMode] = useState('emotion'); // 'emotion' | 'pnl'
  const [view, setView] = useState('monthly'); // 'monthly' | 'yearly'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
  });

  // Build data lookup map (shared by both views)
  const dataMap = useMemo(() => {
    const map = new Map();
    data.forEach(entry => {
      const dateStr = entry.date.split('T')[0];
      map.set(dateStr, entry);
    });
    return map;
  }, [data]);

  // Yearly view: weeks and month labels (UTC-safe)
  const { weeks, monthLabels } = useMemo(() => {
    if (!data.length) return { weeks: [], monthLabels: [] };

    // Find the date range
    const dates = data.map(d => new Date(d.date));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Find the Sunday before minDate (UTC)
    const startDate = new Date(minDate);
    startDate.setUTCDate(startDate.getUTCDate() - startDate.getUTCDay());

    // Find the Saturday after maxDate (UTC)
    const endDate = new Date(maxDate);
    endDate.setUTCDate(endDate.getUTCDate() + (6 - endDate.getUTCDay()));

    // Generate all weeks
    const weeks = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const week = [];

      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const dayDate = new Date(currentDate);
        dayDate.setUTCDate(dayDate.getUTCDate() + dayIdx);

        const dateStr = dayDate.toISOString().split('T')[0];
        const entry = dataMap.get(dateStr);
        week.push(entry || null);
      }

      weeks.push(week);
      currentDate.setUTCDate(currentDate.getUTCDate() + 7);
    }

    // Generate month labels using calculated dates (not data entries)
    const labels = [];
    let lastMonth = -1;

    for (let weekIndex = 0; weekIndex < weeks.length; weekIndex++) {
      const weekStartDate = new Date(startDate);
      weekStartDate.setUTCDate(weekStartDate.getUTCDate() + weekIndex * 7);

      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const dayDate = new Date(weekStartDate);
        dayDate.setUTCDate(dayDate.getUTCDate() + dayIdx);
        const month = dayDate.getUTCMonth();

        if (month !== lastMonth) {
          labels.push({
            weekIndex,
            label: MONTH_NAMES_SHORT[month],
          });
          lastMonth = month;
          break;
        }
      }
    }

    return { weeks, monthLabels: labels };
  }, [data, dataMap]);

  // Monthly view: calendar grid
  const monthGrid = useMemo(() => {
    if (view !== 'monthly' || !data.length) return null;

    const { year, month } = selectedMonth;

    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    const startDow = firstDay.getUTCDay(); // 0=Sun

    const weeks = [];
    let currentWeek = new Array(startDow).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const entry = dataMap.get(dateStr);
      currentWeek.push(entry || { date: dateStr, _empty: true });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }

    return { weeks, title: `${MONTH_NAMES_FULL[month]} ${year}` };
  }, [view, selectedMonth, data, dataMap]);

  const goToPrevMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const getCellColor = (entry) => {
    if (!entry || entry._empty) return NO_DATA_COLOR;

    if (mode === 'pnl') {
      if (entry.profit === null || entry.profit === undefined) return NO_DATA_COLOR;
      if (entry.profit > 0) return PNL_COLORS.positive;
      if (entry.profit < 0) return PNL_COLORS.negative;
      return PNL_COLORS.zero;
    }

    // emotion mode - base on trade dates
    if (entry.has_journal && entry.emotional_state) {
      return EMOTION_COLORS[entry.emotional_state] || TRADED_NO_JOURNAL_COLOR;
    }
    // Has a trade but no journal - show as traded (blue)
    if (entry.profit !== null && entry.profit !== undefined) {
      return TRADED_NO_JOURNAL_COLOR;
    }
    return NO_DATA_COLOR;
  };

  const getCellOpacity = (entry) => {
    if (!entry) return 0.6;
    if (entry._empty) return 0.4;

    if (mode === 'pnl') {
      if (entry.profit === null || entry.profit === undefined) return 0.6;
      return 1; // Full opacity for all trade data
    }

    // emotion mode
    if (entry.has_journal && entry.emotional_state) return 1;
    if (entry.profit !== null && entry.profit !== undefined) return 0.85; // Traded but no journal
    return 0.6;
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground">Emotional State Heatmap</h3>
        <div className="flex items-center gap-3">
          {/* View toggle: Monthly / Yearly */}
          <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
            <button
              onClick={() => setView('monthly')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                view === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setView('yearly')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                view === 'yearly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
            </button>
          </div>
          {/* Emotion / P&L toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
            <button
              onClick={() => setMode('emotion')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                mode === 'emotion'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Emotion
            </button>
            <button
              onClick={() => setMode('pnl')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                mode === 'pnl'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              P&L
            </button>
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No data yet. Start journaling trades to see your emotional heatmap.
        </div>
      ) : (
        <>
          {/* Monthly View */}
          {view === 'monthly' && monthGrid && (
            <div>
              {/* Month navigation */}
              <div className="flex items-center justify-center gap-4 mb-3">
                <button
                  onClick={goToPrevMonth}
                  className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-foreground min-w-[140px] text-center">
                  {monthGrid.title}
                </span>
                <button
                  onClick={goToNextMonth}
                  className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayLabels.map(d => (
                  <div key={d} className="text-[10px] text-muted-foreground text-center">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="space-y-1">
                {monthGrid.weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {week.map((entry, di) => {
                      if (entry === null) {
                        return <div key={di} className="w-full aspect-square" />;
                      }
                      const dayNum = parseInt(entry.date.split('-')[2], 10);
                      const hasTradeData = !entry._empty && entry.profit !== null && entry.profit !== undefined;
                      return (
                        <div
                          key={di}
                          className="w-full aspect-square overflow-hidden rounded-md flex flex-col items-center p-1.5 cursor-pointer transition-transform hover:scale-[1.03]"
                          style={{
                            backgroundColor: getCellColor(entry),
                            opacity: getCellOpacity(entry),
                          }}
                        >
                          <span className="text-base font-bold text-white text-center leading-tight">{dayNum}</span>
                          {hasTradeData ? (
                            <div className="flex flex-col items-center mt-1">
                              <span className="text-[9px] text-white/70 leading-tight capitalize">
                                {entry.emotional_state || (entry.has_journal ? 'Journaled' : 'No journal')}
                              </span>
                              <span className="text-[9px] text-white/50 leading-tight">
                                ${entry.profit.toFixed(2)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[9px] text-white/40 leading-tight mt-1">No trade</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Yearly View */}
          {view === 'yearly' && (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Month labels */}
                <div className="flex mb-1" style={{ paddingLeft: '36px', height: '18px' }}>
                  {monthLabels.map((ml, i) => {
                    const nextWeekIndex = i < monthLabels.length - 1
                      ? monthLabels[i + 1].weekIndex
                      : weeks.length;
                    const span = nextWeekIndex - ml.weekIndex;
                    const widthPx = span * 13 - 2;
                    // Skip labels that are too narrow to display
                    if (widthPx < 20) return null;
                    return (
                      <div
                        key={i}
                        className="text-xs text-muted-foreground flex-shrink-0 overflow-hidden"
                        style={{ width: `${widthPx}px` }}
                      >
                        {ml.label}
                      </div>
                    );
                  })}
                </div>

                {/* Heatmap grid */}
                <div className="flex gap-0.5 pb-2">
                  {/* Day labels */}
                  <div className="flex flex-col gap-0.5 mr-1 flex-shrink-0">
                    {dayLabels.map((label, i) => (
                      <div key={i} className="h-[11px] w-8 text-[10px] text-muted-foreground flex items-center justify-end pr-1">
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Weeks */}
                  <div className="flex gap-0.5">
                    {weeks.map((week, weekIdx) => (
                      <div key={weekIdx} className="flex flex-col gap-0.5">
                        {week.map((entry, dayIdx) => {
                          const hasData = entry !== null;
                          return (
                            <div
                              key={dayIdx}
                              className="w-[11px] h-[11px] rounded-sm cursor-pointer transition-transform hover:scale-150"
                              style={{
                                backgroundColor: getCellColor(entry),
                                opacity: getCellOpacity(entry),
                              }}
                              title={
                                hasData
                                  ? `${entry.date.split('T')[0]}\n${
                                      mode === 'pnl'
                                        ? `P&L: ${entry.profit !== null && entry.profit !== undefined ? '$' + entry.profit.toFixed(2) : 'No trade'}`
                                        : `Emotion: ${entry.emotional_state || 'No journal'}${entry.profit !== null && entry.profit !== undefined ? `\nP&L: $${entry.profit.toFixed(2)}` : ''}`
                                    }`
                                  : 'No data'
                              }
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
            {mode === 'emotion' ? (
              <>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: EMOTION_COLORS.calm }} /> Calm/Confident
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: EMOTION_COLORS.neutral }} /> Neutral
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: EMOTION_COLORS.anxious }} /> Anxious/Frustrated
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: TRADED_NO_JOURNAL_COLOR }} /> Traded (no journal)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: NO_DATA_COLOR, opacity: 0.6 }} /> No trade
                </span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PNL_COLORS.positive }} /> Profit
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PNL_COLORS.negative }} /> Loss
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: NO_DATA_COLOR, opacity: 0.6 }} /> No trade
                </span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EmotionalHeatmap;